import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, IconButton, Navbar as FableNavbar, useViewport } from "@/ds";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/notifications/NotificationBell";
import { useLanguage } from "@/hooks/useLanguage";
import { pathOf, withReturnTo } from "@/lib/returnTo";

/**
 * The app's public header, on Fable (DY1.4).
 *
 * The design system's `Navbar` owns the shape — sticky glass, the 992px shell
 * break (T12-M1), the touch-height rows, the compact EN/ES toggle. This wrapper
 * owns everything the design system cannot know: the routes, the session, and
 * the language persistence.
 *
 * Below 992px the header is the mark alone and the bottom tab bar carries the
 * rest; the `right` slot built here is what the bar's Menú sheet shows instead,
 * and only `compact` (the Dany thread) still renders it in the header.
 *
 * The one thing that DOES ride in the shell header (T14-M1) is the bell, and
 * only for a signed-in reader: notifications are the one channel the tab bar
 * has no cell for, and the Menú sheet is the wrong place for a count you are
 * meant to see without opening anything.
 *
 * `renderLink` hands react-router `Link`s back to the design system, so a nav
 * click stays a client-side navigation instead of reloading the bundle.
 */
export default function Navbar({ compact = false }) {
  const { user, logout } = useAuth();
  const { isNavMobile } = useViewport();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { lang, setLanguage } = useLanguage();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const role = user?.profile?.role;
  const dashboardPath =
    role === "provider" ? "/provider/dashboard" : role === "admin" ? "/admin" : "/client/dashboard";

  const links = [
    { href: "/providers", label: t("nav.find") },
    { href: "/open-jobs", label: t("nav.openJobs") },
    { href: "/pricing", label: t("nav.pricing") },
    { href: "/how-it-works", label: t("nav.howItWorks") },
  ];

  // Compact keeps the same controls in the bar on a 360px phone, so the labels
  // have to be the short ones — "Iniciar sesión" and "Regístrate" together are
  // ~175px, which is the whole budget the mark and the toggle leave behind.
  // Signed in, two labelled buttons cannot fit either: the dashboard becomes an
  // icon and logout moves to the dashboard, which is where it lives anyway.
  const tight = compact && isNavMobile;
  const right = user ? (
    tight ? (
      // IconButton renders a <button>, not an anchor — `as` would land on the
      // DOM node and navigate nowhere.
      <IconButton icon="user" label={t("nav.dashboard")}
        onClick={() => navigate(dashboardPath)} data-testid="nav-dashboard" />
    ) : (
      <>
        <Button as={Link} to={dashboardPath} variant="ghost" size="sm" icon="user" data-testid="nav-dashboard">
          {t("nav.dashboard")}
        </Button>
        <Button variant="ghost" size="sm" icon="log-out" onClick={handleLogout} data-testid="nav-logout">
          {t("nav.logout")}
        </Button>
      </>
    )
  ) : (
    <>
      {/* Sign IN returns you to the page you were reading — you were already
          doing something. Sign UP deliberately does not: a brand-new account
          belongs on its own dashboard, not back on /faq. */}
      <Button as={Link} to={withReturnTo("/login", pathOf(location))} variant="ghost" size="sm" data-testid="nav-login">
        {tight ? t("nav.loginShort") : t("nav.login")}
      </Button>
      {/* Secondary (REG-A): the navbar is on every screen, and every screen
          keeps its one primary for its own job. */}
      <Button as={Link} to="/signup/client" variant="secondary" size="sm" data-testid="nav-signup">
        {tight ? t("nav.signupShort") : t("nav.signup")}
      </Button>
    </>
  );

  return (
    /* The sticky lives HERE, not on the design system's <header> (DY1.4).
       It was on the header all along and did nothing on any page: a sticky
       element travels inside its containing block, and this wrapper is exactly
       as tall as the header it wraps — 64px of room to move in. Measured on the
       deployed site, `top` equalled `-scrollY` on all eight public routes at
       both widths, which is a header not sticking at all. One level up, the
       containing block is the page, and it holds. */
    <div data-testid="navbar" style={{ position: "sticky", top: 0, zIndex: 40 }}>
      <FableNavbar
        compact={compact}
        links={compact && isNavMobile ? [] : links}
        activeHref={location.pathname}
        lang={lang}
        onLangChange={setLanguage}
        menuLabel={t("nav.toggleMenu")}
        right={right}
        /* Rendered only in the shell bar (below 992px, and not `compact`).
           Above 992px this header is untouched — a public page has never
           carried a bell there, and the dashboard's own desktop row does. */
        shellRight={user ? <NotificationBell /> : null}
        renderLink={({ href, label, style, key, onClose }) => (
          <Link
            key={key}
            to={href}
            style={style}
            onClick={onClose}
            data-testid={`nav-${href.replace(/\//g, "")}`}
          >
            {label}
          </Link>
        )}
      />
    </div>
  );
}
